/* @jsxImportSource @emotion/react */

import type React from 'react'
import { css } from '@emotion/react'
import { useIntl } from 'react-intl'

import { SliteLogo } from '@slite/components'
import { ChevronIcon, ClaudeIcon, SlackIcon } from '@slite/icons'

import { GrainCard } from '../../components/GrainCard'
import { Section } from '../../components/Section'
import { TextLayout } from '../../components/TextLayout'
import { WebsiteText } from '../../components/WebsiteText'
import { useRoutes } from '../../routes'
import { MEDIUM_BREAKPOINT } from '../../utils/breakpoints'
import { ChromeLogo } from './interfaces/ChromeLogo'
import { ClaudeCodeInterface } from './interfaces/ClaudeCodeInterface'
import { IntercomInterface } from './interfaces/IntercomInterface'
import { SlackInterface } from './interfaces/SlackInterface'
import { SliteInterface } from './interfaces/SliteInterface'

const VISUAL_HEIGHT = 280
// Windows render taller than their slot so the card edge crops them.
const VISUAL_BLEED = 16

type AgentInterface = {
  id: string
  icon: React.ReactNode
  visual: React.ReactNode
  title: string
  description: string
  link?: { href: string; label: string; isExternal?: boolean }
}

const gridCss = css`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 32px;

  @media (max-width: ${MEDIUM_BREAKPOINT - 1}px) {
    grid-template-columns: 1fr;
  }
`

const colCss = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
  color: inherit;
  text-decoration: none;
`

const cardCss = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 32px 32px 0;
  transition: transform 0.25s ease;

  a:hover > & {
    transform: translateY(-4px);
  }
`

const visualCss = css`
  margin-top: 32px;
  height: ${VISUAL_HEIGHT}px;
  pointer-events: none;
  user-select: none;
`

const visualInnerCss = css`
  height: ${VISUAL_HEIGHT + VISUAL_BLEED}px;
`

const cardIconCss = css`
  width: 20px;
  height: 20px;
`

const cardTitleCss = css`
  font-size: 20px;
  line-height: 1.3;
`

const cardDescriptionCss = css`
  max-width: 440px;
`

export function SectionAgentInterfaces() {
  const intl = useIntl()
  const routes = useRoutes()

  const interfaces: AgentInterface[] = [
    {
      id: 'slite',
      icon: <SliteLogo variant="symbol" css={cardIconCss} />,
      visual: <SliteInterface />,
      title: intl.formatMessage({
        id: 'agent.interfaces.slite.title',
        defaultMessage: 'Slite',
      }),
      description: intl.formatMessage({
        id: 'agent.interfaces.slite.desc',
        defaultMessage:
          'Ask questions and turn answers into maintained documentation.',
      }),
      link: {
        href: routes.auth.signup(),
        label: intl.formatMessage({
          id: 'agent.interfaces.slite.link',
          defaultMessage: 'Open app',
        }),
      },
    },
    {
      id: 'slack',
      icon: <SlackIcon size={18} />,
      visual: <SlackInterface />,
      title: intl.formatMessage({
        id: 'agent.interfaces.slack.title',
        defaultMessage: 'Slack',
      }),
      description: intl.formatMessage({
        id: 'agent.interfaces.slack.desc',
        defaultMessage:
          'Get cited answers in the thread and bring decisions back into your docs.',
      }),
      link: {
        href: routes.external.helpCenter.url,
        label: intl.formatMessage({
          id: 'agent.interfaces.slack.link',
          defaultMessage: 'Get in Slack',
        }),
        isExternal: true,
      },
    },
    {
      id: 'browser-extension',
      icon: <ChromeLogo size={18} />,
      visual: <IntercomInterface />,
      title: intl.formatMessage({
        id: 'agent.interfaces.extension.title',
        defaultMessage: 'Google Chrome',
      }),
      description: intl.formatMessage({
        id: 'agent.interfaces.extension.desc',
        defaultMessage:
          'Use Slite Agent in any tool, with the page in front of you as context.',
      }),
      link: {
        href: routes.browserExtension(),
        label: intl.formatMessage({
          id: 'agent.interfaces.extension.link',
          defaultMessage: 'Get it',
        }),
      },
    },
    {
      id: 'mcp',
      icon: <ClaudeIcon size={20} />,
      visual: <ClaudeCodeInterface />,
      title: intl.formatMessage({
        id: 'agent.interfaces.mcp.title',
        defaultMessage: 'MCP',
      }),
      description: intl.formatMessage({
        id: 'agent.interfaces.mcp.desc',
        defaultMessage:
          'Bring trusted company knowledge into Claude, Codex, Cursor and more.',
      }),
      link: {
        href: routes.mcp(),
        label: intl.formatMessage({
          id: 'agent.interfaces.mcp.link',
          defaultMessage: 'Set up the MCP',
        }),
      },
    },
  ]

  return (
    <Section background="moon-dust-lighter" containerVerticalPadding={80}>
      <div className="text-align-center">
        <TextLayout
          type="centered"
          title={intl.formatMessage({
            id: 'agent.interfaces.title',
            defaultMessage: 'Put trusted context to work across your company',
          })}
          titleMaxWidth={500}
        />
      </div>
      <div css={gridCss}>
        {interfaces.map((item) => {
          const Col = item.link ? 'a' : 'div'
          return (
            <Col
              key={item.id}
              css={colCss}
              {...(item.link && {
                href: item.link.href,
                'aria-label': item.link.label,
                ...(item.link.isExternal && {
                  target: '_blank',
                  rel: 'noreferrer',
                }),
              })}
            >
              <GrainCard css={cardCss}>
                <div className="flexbox flexbox-align-center flexbox-gap-12">
                  {item.icon}
                  <WebsiteText
                    variant="smallTitle"
                    htmlTag="h3"
                    className="font-weight-bold"
                    css={cardTitleCss}
                  >
                    {item.title}
                  </WebsiteText>
                  {item.link && (
                    <span
                      className="flexbox flexbox-align-center flexbox-gap-6 shade-00 font-size-6 font-weight-medium margin-left-auto padding-top-2"
                      aria-hidden
                    >
                      {item.link.label}
                      <ChevronIcon size={16} />
                    </span>
                  )}
                </div>
                <p
                  className="font-size-6 shade-10 line-height-large margin-top-16 font-weight-regular"
                  css={cardDescriptionCss}
                >
                  {item.description}
                </p>
                <div css={visualCss}>
                  <div css={visualInnerCss}>{item.visual}</div>
                </div>
              </GrainCard>
            </Col>
          )
        })}
      </div>
    </Section>
  )
}
